"use client";

import { useSyncExternalStore } from "react";
import { useTranslations } from "next-intl";
import { supportStatus, type SupportStatus as Status } from "@/lib/support-hours";

// The wall clock is external mutable state, so it is read through
// useSyncExternalStore rather than mirrored into useState from an effect.
//
// Client-side on purpose: these pages are cached for an hour, so a status
// computed on the server would go on claiming "nu bereikbaar" through the
// night. getServerSnapshot returns null, which renders a placeholder during
// SSR and hydration and fills in immediately after.

let cached: Status | null = null;
let cachedKey = "";

/**
 * A referentially stable snapshot.
 *
 * useSyncExternalStore compares snapshots by identity and re-renders forever
 * if a new object comes back every call, which supportStatus() does. Keying on
 * the meaningful value keeps the same object until the status actually changes.
 */
function getSnapshot(): Status {
  const next = supportStatus();
  const key = next.open ? "open" : `${next.opensAt}|${next.opensOn}`;

  if (key !== cachedKey || cached === null) {
    cachedKey = key;
    cached = next;
  }

  return cached;
}

function getServerSnapshot(): Status | null {
  return null;
}

function subscribe(onChange: () => void): () => void {
  // A minute is plenty of granularity for an opening time, and it means
  // someone reading at 17:29 is not still told we are open at 17:31.
  const timer = setInterval(onChange, 60_000);

  return () => clearInterval(timer);
}

export function SupportStatus() {
  const t = useTranslations("Klantenservice.router.status");
  const status = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Hold the row's height before the answer is known, so nothing jumps.
  if (status === null) {
    return <span className="inline-flex h-5" aria-hidden="true" />;
  }

  if (status.open) {
    return (
      <span className="inline-flex items-center gap-2 text-sm font-medium text-success-dark">
        <span className="relative flex size-2">
          <span className="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-60" />
          <span className="relative inline-flex size-2 rounded-full bg-success" />
        </span>
        {t("open")}
      </span>
    );
  }

  const label =
    status.opensOn === "today"
      ? t("closedToday", { time: status.opensAt })
      : status.opensOn === "tomorrow"
        ? t("closedTomorrow", { time: status.opensAt })
        : t("closedOn", { day: t(`days.${status.opensOn}`), time: status.opensAt });

  return (
    <span className="inline-flex items-center gap-2 text-sm font-medium text-muted">
      <span className="size-2 rounded-full bg-muted/50" />
      {label}
    </span>
  );
}
